// @ts-strict

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IPagination, ISingleCampaign, IBannerConfigurationData, ICreatedConfigurationConfigData, SpecialCampaignData } from '@app/core/models/core-models';
import { BannersService } from '@app/core/services/banners/banners.service';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { IBannerConfigData, IBannerData, IBannerSizeData } from '@app/core/services/banners/banners.service.types';
import { GlobalNotificationsService } from '@app/core/services/global-notifications/global-notifications.service';
import { NotificationTypes } from '@app/core/services/global-notifications/global-notifications.model';

@Component({
  selector: 'app-banners-list-modal',
  templateUrl: './banners-list-modal.component.html',
  styleUrls: ['./banners-list-modal.component.scss']
})
export class BannersListModalComponent implements OnInit {
  @Input() currentCampaign?: ISingleCampaign | SpecialCampaignData;
  @Input() bannerConfigOnly? = false;
  @Input() selectedBannerData: IBannerData | undefined;
  @Output() bannerConfigVisible = new EventEmitter<boolean>();

  public isBannerConfigVisible = false;
  public noWayBack = false;
  public isGeneratedURLVisible = false;
  public bannersList: Array<IBannerData> = [];
  public bannersAvailableSizes: Array<IBannerSizeData> = [];
  public isLoaded = false;
  public scrolledToBottom = false;
  public isSuccessfullySubmitted = false;
  public selectBannerSizeForm = this.fb.group({
    bannerSize: [null, Validators.required]
  });
  public bannerConfigurationData: IBannerConfigData = {
    bannerConfigID: 0,
    domainID: 0,
    programID: 0
  };

  private currentResolution = '';
  private bannersPagination?: IPagination;

  constructor(
    private bannersService: BannersService,
    private globalNotificationsService: GlobalNotificationsService,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    if (this.bannerConfigOnly && this.selectedBannerData) {
      this.noWayBack = true;
      this.switchSelectedBanner(this.selectedBannerData);
      this.isLoaded = true;
    } else {
      this.bannersService.getBannersSizes(this.currentCampaign?.id || 0).subscribe(res => {
        this.bannersAvailableSizes = res.data?.banner_sizes || [];
        this.selectBannersSize(this.bannersAvailableSizes[0]);
      });
    }
  }

  private getBanners(page: number, resolution: string): void {
    this.bannersService.getBannersList(this.currentCampaign?.id || 0, page, resolution).subscribe(res => {
      const banners = res.data?.banners || [];
      if (resolution && page === 1) {
        this.bannersList = banners;
      } else {
        this.bannersList.push(...banners);
      }
      this.bannersPagination = res.data?.pagination;
      if (this.bannersList) {
        this.isLoaded = true;
      }
    });
  }

  public selectBannersSize(bannersSize: IBannerSizeData): void {
    this.currentResolution = bannersSize.resolution || '';
    this.isLoaded = false;
    this.selectBannerSizeForm.patchValue({ bannerSize: bannersSize.resolution });
    this.getBanners(1, bannersSize.resolution || '');
  }

  public switchSelectedBanner(selectedBannerData: IBannerData): void {
    this.isBannerConfigVisible = true;
    this.bannerConfigVisible.emit(true);
    this.selectedBannerData = selectedBannerData;
  }

  public changeVisibility(event: boolean): void {
    this.isBannerConfigVisible = event;
    this.bannerConfigVisible.emit(false);
  }

  public back(): void {
    this.isBannerConfigVisible = false;
    this.isGeneratedURLVisible = false;
    this.changeVisibility(false);
  }

  public addBanner(data: ICreatedConfigurationConfigData): void {
    this.bannersService
      .addBannerConfiguration(data.config.id || 0, this.selectedBannerData?.id || 0, data.trackingDomain.id, this.currentCampaign?.id || 0)
      .subscribe(
        res => {
          this.showNotification('banner_added_successfully', NotificationTypes[0]);
          this.bannerConfigurationData = {
            bannerConfigID: res.data?.configuration_id || 0,
            domainID: data.trackingDomain.id,
            programID: this.currentCampaign?.id || 0
          };

          const addedBanner: IBannerConfigurationData = {
            banner_id: res.data?.banner_id || 0,
            updated_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
            configuration_id: res.data?.configuration_id || 0,
            height: this.selectedBannerData?.height || 0,
            width: this.selectedBannerData?.width || 0,
            img: this.selectedBannerData?.img || '',
            program_config_name: data.config.name
          };

          this.isSuccessfullySubmitted = true;

          this.bannersService.newBanner.next(addedBanner);
        },
        err => {
          this.showNotification('error_occured', NotificationTypes[1]);
        }
      );
  }

  public checkIfScrolled(event: Event): void {
    const container = event.target as HTMLElement;

    const scrollDiff = container.scrollTop - (container.scrollHeight - container.clientHeight);
    this.scrolledToBottom = scrollDiff > -20;

    if (this.scrolledToBottom && (this.bannersPagination?.total_pages || 1) > 1) {
      this.getBanners((this.bannersPagination?.current_page || 1) + 1, this.currentResolution);
    }
  }

  private showNotification(message: string, type: (typeof NotificationTypes)[number], translate: boolean = true): void {
    this.globalNotificationsService.show({
      message: translate && !message.includes('view_translations.') ? `view_translations.${message}` : message,
      type,
      translate,
      isPersistent: false,
      id: ''
    });
  }
}
